import * as Dialog from '@radix-ui/react-dialog';
import { AdaptivePhoto } from '@/components/photos/AdaptivePhoto';
import { MarkupCanvas } from '@/components/markup/MarkupCanvas';
import type { MarkupPreviewModalProps } from './MarkupPreviewModal.types';
import logger from '@/utils/logger';
import { useEffect } from 'react';

export function MarkupPreviewModal({
  isOpen,
  onClose,
  photo,
  shapes,
}: MarkupPreviewModalProps) {
  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        logger.info('MarkupPreviewModal', 'Closing via ESC key');
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Calculate photo aspect ratio for MarkupCanvas
  const photoAspectRatio = (() => {
    const variant = photo.variants?.find((v) => v.width && v.height);
    if (variant && variant.width && variant.height) {
      return variant.width / variant.height;
    }
    return 1;
  })();

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/80 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 h-[85vh] w-[85vw] translate-x-[-50%] translate-y-[-50%] flex flex-col rounded-lg border border-border bg-background p-6 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <Dialog.Title className="text-lg font-semibold text-foreground">
              Markup Preview
            </Dialog.Title>
            <Dialog.Close className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span className="sr-only">Close</span>
            </Dialog.Close>
          </div>

          {/* Description */}
          <Dialog.Description className="text-sm text-muted mb-4 flex-shrink-0">
            Photo with {shapes.length} markup annotation{shapes.length !== 1 ? 's' : ''}.
          </Dialog.Description>

          {/* Photo with markup - fills available space */}
          <div className="flex-1 mb-4 overflow-hidden">
            <div className="relative w-full h-full items-center justify-center bg-surface-2 rounded">
              <AdaptivePhoto photo={photo} className="w-full h-full object-contain" />

              {/* Markup canvas overlay */}
              <div className="absolute inset-0 w-full h-full pointer-events-none">
                <MarkupCanvas
                  shapes={shapes}
                  selectedShapeId={null}
                  readOnly={true}
                  photoAspectRatio={photoAspectRatio}
                />
              </div>
            </div>
          </div>

          {/* Shape details */}
          <div className="p-4 bg-surface-1 rounded border border-border flex-shrink-0">
            <h4 className="text-sm font-medium text-foreground mb-2">
              Markup Details ({shapes.length} shape{shapes.length !== 1 ? 's' : ''})
            </h4>
            <div className="flex flex-wrap gap-2">
              {shapes.map((shape) => (
                <div
                  key={shape.id}
                  className="inline-flex items-center gap-2 px-2 py-1 bg-surface-2 rounded text-xs"
                >
                  <span className="font-bold text-primary">#{shape.number}</span>
                  <span className="text-muted capitalize">{shape.type}</span>
                  <div
                    className="w-4 h-4 border rounded"
                    style={{
                      borderColor: shape.strokeColor,
                      borderWidth: 2,
                      borderStyle: shape.strokeDasharray ? 'dashed' : 'solid',
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
